import { postMessageToParent } from '@/utils/postMessageToParent';
import { HeroesController } from './HeroesController';
import { SpineLayout } from '@pixi/spine-layout';
import type { GameState, HeroData, RaceTick, UpdateEvent } from '../helpers/types';
import { EventEmitter } from '../controllers/EventEmitter';
import { store } from '../store/store';
import { config } from '../settings/config';
import { TrackController } from './TrackController';
import { StoreSyncController } from './StoreSyncController';
import { raceLogger } from '../utils/RaceLogger';
import { getTrackedHero } from '../utils/gameUtils';
import { PositionsController } from './PositionsController';
import { wait } from '@/utils/wait';
import { sounds } from '@/utils/Sounds';
import { useSettings } from '@/store/settingsStore';

const { getState, subscribe } = store;

export class GameController {
    private heroes: HeroesController;
    private track: TrackController;
    private positions: PositionsController;
    private finishTriggered = false;
    private winTriggered = false;

    constructor(layout: SpineLayout) {
        this.track = new TrackController(layout);
        this.heroes = new HeroesController(layout);
        this.positions = new PositionsController(this.heroes);

        this.addEvents();

        this.track.hide(true);

        const state = getState().gameState;

        if (state !== 'CELEBRATION') {
            this.track.show();
        }
    }

    private addEvents() {
        new StoreSyncController();

        EventEmitter.on('horses:tick', (payload) => {
            this.updateState(payload as UpdateEvent);
            this.onTick(payload);
        });

        EventEmitter.on('horses:update', (payload) => {
            this.updateState(payload);
            this.onTick(payload);
        });

        subscribe(
            (state) => state.selectedRacer,
            (selectedRacer) => {
                const state = getState().gameState;

                if (state === 'PLACE_BET') {
                    this.heroes.unselectAll();
                    this.heroes.parade.selectHero(selectedRacer);
                }
            },
        );

        subscribe(
            (state) => state.gameState === 'PLACE_BET',
            () => this.heroes.unselectAll(),
        );

        subscribe(
            (state) => state.betPlaced,
            (betPlaced) => {
                if (betPlaced) {
                    if (getState().gameState === 'RACE') {
                        // Bet restored on reconnect — race already running, just highlight racer
                        this.heroes.selectedHero(getState().betOnRacer);
                        return;
                    }
                    this.prepareRace();
                    this.track.prepareForTheRace();
                    postMessageToParent({ type: 'gameRoundStarted' });
                }
            },
        );

        useSettings.subscribe((state, prev) => {
            if (state.muted !== prev.muted) {
                sounds.updateSettings({ muted: state.muted });
            }
        });
    }

    private startParade() {
        this.resetRaceRuntimeState();
        this.positions.reset();

        const activeHeroes = getState().activeHeroes;

        if (activeHeroes.length === 0) {
            console.warn('No heroes data available to start parade.');

            return;
        }

        this.heroes.startParade(activeHeroes.map((hero: HeroData) => hero.name));
        this.track.showStartLine();
    }

    private prepareRace() {
        const activeIDs = getState().activeHeroes.map((horse) => horse.name);

        this.resetRaceRuntimeState();
        this.heroes.stopParade();
        this.heroes.positionRacersOnTracks(activeIDs);
        this.positions.reset();
    }

    private async onTick(payload: UpdateEvent | RaceTick) {
        raceLogger.logTick(payload, getState().activeHeroes);

        switch (payload.gameState) {
            case 'PLACE_BET':
                getState().setBetProgress(payload.raceTick / config.ticksAmount.placeBet);
                break;
            case 'RACE':
                await this.onRaceTick();
                break;
        }
    }

    private updateState(payload: UpdateEvent) {
        if (payload.horses) {
            getState().setActiveHeroes(payload.horses);
        }

        getState().setStateTick(payload.raceTick);

        if (payload.gameState !== getState().gameState) {
            this.onStateChange(payload.gameState, getState().gameState);
            getState().setGameState(payload.gameState);
            getState().setRaceID(payload.raceId);

            if (payload.gameState === 'CELEBRATION') {
                postMessageToParent({ type: 'gameRoundEnded' });
            }
        }
    }

    private async onStateChange(state: GameState, from: GameState) {
        console.log(`!!! State: ${from} -> ${state}`);

        switch (state) {
            case 'PLACE_BET':
                if (from === 'CELEBRATION' || from === 'INIT') {
                    this.track.show();
                }


                sounds.playMusic('bet');
                this.startParade();
                getState().resetRound();
                this.heroes.selectedHero(getState().selectedRacer);
                break;

            case 'NO_MORE_BETS':
                if (from === 'PLACE_BET') {
                    wait(500).then(() => {
                        this.track.startCountDown();
                    });
                }
                this.prepareRace();

                if (!getState().betPlaced) {
                    getState().selectRacer('');
                }

                this.heroes.selectedHero(getState().selectedRacer);
                break;

            case 'RACE':
                if (from !== 'NO_MORE_BETS') {
                    this.prepareRace();
                } else {
                    this.track.start();
                }

                sounds.playMusic('race');

                this.resetRaceRuntimeState();
                this.track.move();
                this.heroes.setAllRun();
                this.heroes.selectedHero(getState().selectedRacer || getState().betOnRacer);
                break;

            case 'CELEBRATION':
                sounds.playMusic('finish');

                raceLogger.printReport();
                break;
        }
    }

    private async onRaceTick() {
        const { activeHeroes, selectedRacer, betOnRacer } = getState();
        const trackedHero = getTrackedHero(activeHeroes, selectedRacer, betOnRacer);

        this.heroes.handlePositionChanges(activeHeroes);
        this.positions.update(activeHeroes, trackedHero);

        if (!this.finishTriggered && trackedHero && trackedHero.distance >= config.finishDistance) {
            this.finishTriggered = true;
            await this.track.finish();
            this.positions.release();
        }

        if (!this.winTriggered && trackedHero && trackedHero.distance >= config.maxDistance) {
            this.winTriggered = true;
            this.heroes.showWinner(trackedHero.name);
        }
    }

    private resetRaceRuntimeState() {
        this.finishTriggered = false;
        this.winTriggered = false;
        this.heroes.resetPositionTracking();
    }
}
